import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { NotificationEnableButton } from "@/components/notifications/NotificationEnableButton";
import {
    BarChart3,
    Bell,
    Bike,
    Boxes,
    Building2,
    ClipboardList,
    LayoutDashboard,
    ListChecks,
    LogOut,
    Menu,
    Package,
    ShieldCheck,
    Store,
    Tag,
    Ticket,
    Truck,
    UserPlus,
    Users,
    Wallet,
    Zap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/AuthContext";
import { adminApi, type AdminSession } from "@/features/admin/api";
import { ROUTES } from "@/constants/routes";
import { LoadingScreen } from "@/components/common/LoadingScreen";
import { cn } from "@/lib/utils";

type NavItem = {
    to: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    perm?: keyof AdminSession["permissions"];
};

const NAV: NavItem[] = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/products", label: "Products", icon: Boxes, perm: "manage_products" },
    { to: "/admin/categories", label: "Categories", icon: Tag, perm: "manage_categories" },
    { to: "/admin/inventory", label: "Inventory", icon: BarChart3, perm: "manage_products" },
    { to: "/admin/orders", label: "Orders", icon: Package },
    { to: "/admin/customers", label: "Customers", icon: Users, perm: "manage_customers" },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    // Global/dispatch — gated to super_admin + operations_admin only
    { to: "/admin/dispatch", label: "Dispatch", icon: Zap, perm: "view_activity" },
    { to: "/admin/live-map", label: "Live map", icon: Truck, perm: "view_activity" },
    { to: "/admin/riders", label: "Riders", icon: Bike, perm: "manage_team" },
    { to: "/admin/stores", label: "Stores", icon: Building2, perm: "manage_stores" },
    { to: "/admin/team", label: "Team", icon: UserPlus, perm: "manage_team" },
    { to: "/admin/wallet", label: "Wallets", icon: Wallet, perm: "manage_team" },
    { to: "/admin/settlements", label: "Cash settlements", icon: Wallet, perm: "view_activity" },
    { to: "/admin/attendance", label: "Attendance & payroll", icon: ClipboardList, perm: "view_activity" },
    { to: "/admin/activity", label: "Activity log", icon: ListChecks, perm: "view_activity" },
    { to: "/admin/coupons", label: "Coupons", icon: Ticket, perm: "manage_coupons" },
    { to: "/admin/notifications", label: "Notifications", icon: Bell, perm: "manage_notifications" },
    // Store admin essentials
    { to: "/admin/store-settings", label: "Store settings", icon: Store },
    { to: "/admin/profile", label: "Profile", icon: ShieldCheck },
];

export function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const { data: session, isLoading, isError } = useQuery({
        queryKey: ["admin", "session"],
        queryFn: adminApi.session,
        retry: false,
    });

    if (isLoading) return <LoadingScreen label="Verifying admin session…" />;

    if (isError || !session) {
        // Send back to the appropriate portal for whichever role they were.
        const target =
            user?.role === "store_admin"
                ? ROUTES.storeLogin
                : user?.role === "operations_admin"
                  ? ROUTES.opsLogin
                  : user?.role === "staff"
                    ? ROUTES.staffLogin
                    : ROUTES.superAdminLogin;
        navigate(target, { replace: true });
        return null;
    }

    const onLogout = async () => {
        await logout();
        toast.success("Signed out");
        const target =
            user?.role === "store_admin"
                ? ROUTES.storeLogin
                : user?.role === "operations_admin"
                  ? ROUTES.opsLogin
                  : user?.role === "staff"
                    ? ROUTES.staffLogin
                    : ROUTES.superAdminLogin;
        navigate(target);
    };

    const visibleNav = NAV.filter((n) => !n.perm || session.permissions[n.perm]);

    return (
        <div data-testid="admin-layout" className="flex min-h-screen bg-ink text-white">
            {/* Sidebar */}
            <aside
                className={cn(
                    "fixed inset-y-0 left-0 z-40 w-64 shrink-0 flex-col border-r border-white/5 bg-[#0b0f14] px-3 py-6 lg:static lg:flex",
                    open ? "flex" : "hidden lg:flex",
                )}
            >
                <div className="flex items-center gap-2 px-3 pb-6">
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand text-white">
                        <ShieldCheck className="h-5 w-5" />
                    </span>
                    <div>
                        <p className="text-sm font-bold">TSGo Console</p>
                        <p className="text-[11px] uppercase tracking-widest text-white/50">
                            {session.role.replace("_", " ")}
                        </p>
                    </div>
                </div>

                <nav className="flex-1 space-y-1">
                    {visibleNav.map(({ to, label, icon: Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            onClick={() => setOpen(false)}
                            data-testid={`admin-nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                cn(
                                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition",
                                    isActive
                                        ? "bg-brand/15 text-brand"
                                        : "text-white/70 hover:bg-white/5 hover:text-white",
                                )
                            }
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="mt-6 rounded-xl border border-white/5 bg-white/5 p-3 text-xs">
                    <p className="font-semibold text-white/90 truncate">{user?.full_name}</p>
                    <p className="text-white/50 truncate">{user?.email}</p>
                    <button
                        type="button"
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-error/20 py-1.5 text-error hover:bg-error/30"
                    >
                        <LogOut className="h-4 w-4" /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex min-w-0 flex-1 flex-col bg-[#f5f6f8] text-ink">
                <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border/70 bg-white/95 px-4 py-3 backdrop-blur lg:px-8">
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        className="grid h-9 w-9 place-items-center rounded-lg border border-border lg:hidden"
                        data-testid="admin-menu-toggle"
                    >
                        <Menu className="h-4 w-4" />
                    </button>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Truck className="h-4 w-4 text-brand" />
                        <span>Preview environment</span>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1 text-xs font-bold text-brand">
                        {session.role.replace("_", " ").toUpperCase()}
                    </span>
                    <NotificationEnableButton />
                    <NotificationBell />
                </header>

                <main className="flex-1 p-4 lg:p-8">
                    <Outlet context={session} />
                </main>
            </div>
        </div>
    );
}

export default AdminLayout;
